<template>
    <div class="customizer dashboard-item glass" :style="overlayStyles">
        <h1 class="customizer-title">Customize your profile</h1>

        <!-- Titles -->
        <section class="customizer-section">
            <h2 class="customizer-heading">Title</h2>
            <p class="customizer-hint">
                Shown under your name. Earned from achievements.
            </p>

            <div v-if="titles.length" class="title-options">
                <button
                    type="button"
                    class="title-option"
                    :class="{ 'is-selected': selectedTitle === null }"
                    @click="selectedTitle = null">
                    No title
                </button>
                <button
                    v-for="option in titles"
                    :key="option.id"
                    type="button"
                    class="title-option"
                    :class="[
                        `is-${option.rarity}`,
                        // `is-outlined` is what tells the global tier
                        // rules in variables.scss not to fill this — see
                        // the note there. Without it an unselected pill
                        // keeps the tier gradient and looks selected.
                        selectedTitle === option.id ? 'is-selected' : 'is-outlined',
                    ]"
                    @click="selectedTitle = option.id">
                    {{ option.name }}
                </button>
            </div>
            <p v-else class="customizer-empty">
                No titles yet — unlock achievements by playing to earn some.
            </p>
        </section>

        <!-- Backgrounds -->
        <section class="customizer-section">
            <h2 class="customizer-heading">Background</h2>
            <p class="customizer-hint">
                <!-- Explicitly info rather than inheriting the link colour,
                     which is $primary ($link: $primary in variables.scss). -->
                Unlock more in the
                <router-link class="has-text-info" to="/shop">shop</router-link>.
            </p>

            <div class="theme-options">
                <button
                    v-for="option in themeOptions"
                    :key="option.id"
                    type="button"
                    class="theme-option"
                    :class="[
                        `is-tier-${option.rarity}`,
                        { 'is-selected': selectedTheme === option.id, 'is-locked': !option.owned },
                    ]"
                    :disabled="!option.owned"
                    :title="option.owned ? option.name : `${option.name} — not unlocked yet`"
                    @click="option.owned && (selectedTheme = option.id)">
                    <!-- The swatch is the background itself rather than a
                         colour chip, so what's previewed is what applies. -->
                    <span class="theme-swatch" :style="option.swatchStyle">
                        <span class="theme-swatch-card" :style="{ backgroundColor: option.overlay }"></span>
                        <b-icon v-if="!option.owned" class="theme-lock" icon="lock" size="is-small" />
                    </span>
                    <!-- Tinted by tier, so the ladder is readable in the
                         picker without a legend. -->
                    <span class="theme-name">{{ option.name }}</span>
                </button>
            </div>
        </section>

        <!-- Privacy -->
        <section class="customizer-section">
            <h2 class="customizer-heading">Privacy</h2>

            <label class="privacy-toggle">
                <b-switch v-model="hideProfile" />
                <span class="privacy-toggle-text">
                    <span class="privacy-toggle-label">Hide profile from other users</span>
                    <span class="customizer-hint">
                        Anyone who opens your profile link sees only that it's
                        hidden. You can still see it yourself, and this doesn't
                        affect the leaderboard.
                    </span>
                </span>
            </label>
        </section>

        <!-- Managing the subscription lives here rather than on /premium or
             in the nav's account menu. Both were the wrong home: the sales
             page is for people who don't pay yet, and the menu grew an extra
             entry only for subscribers, so it changed shape depending on who
             opened it. This tab is already "the things you own and control".

             Gated on `hasBilling` rather than `isPremium`, which is a
             different question in two directions. A subscriber whose card
             bounces goes `past_due`: not entitling, so `premium` is false
             within the hour — while Stripe retries for up to three weeks and
             the card update that fixes it lives behind this button. Keying
             this off `premium` hid the door at exactly the moment it was
             needed, leaving "buy another subscription" as the only visible
             action. In the other direction, being premium does not mean
             Stripe is behind it, which is what the branches below sort out.

             A free player still sees nothing here; /premium is where they
             are meant to go. -->
        <section v-if="hasBilling" class="customizer-section">
            <h2 class="customizer-heading">Gamebot Premium</h2>

            <!-- Why they were sent here, when they did not choose to come.
                 A refusal that redirects loses its own message: the store's
                 fetch wrapper navigates on `redirect` and never throws, so
                 the `error` beside it is dropped and the trip looks like the
                 button silently failing. -->
            <p v-if="billingNotice" class="billing-notice">
                {{ billingNotice }}
            </p>

            <p v-if="hasStripeBilling" class="customizer-hint">
                Change your card, see invoices, or cancel. Handled by Stripe,
                so payment details never touch this site.
            </p>

            <!-- Bought through Discord: there is no Stripe customer behind
                 this account, so the portal has nothing to show and would
                 answer "no subscription found to manage" — an error for doing
                 what the page just invited them to do. Discord bills it in
                 its own client and exposes no external URL that opens the
                 right screen, so the honest answer is to say where it lives
                 and link Discord's own instructions. -->
            <p v-else-if="paysViaDiscord" class="customizer-hint">
                You're subscribed through Discord, so your billing lives there
                rather than here — open <strong>User Settings → Subscriptions</strong>
                in Discord to change your payment method or cancel.
                <a :href="discordSubscriptionHelpUrl" target="_blank" rel="noopener noreferrer">
                    Discord's guide</a> walks through it.
            </p>

            <!-- The only rail left. `premium` is derived as "any rail
                 active", so a premium user always has at least one source,
                 and the two above have already claimed Stripe and Discord. -->
            <p v-else class="customizer-hint">
                Your Premium was granted directly rather than bought, so there
                is no billing attached to it and nothing to manage.
            </p>

            <!-- No FontAwesome crown here. The premium crown is the brand's
                 own SVG everywhere it appears (the nav, the profile card, the
                 sales page) because FA's is a different shape and carries none
                 of the gold — and a button is the one place it would sit at
                 text size, where the difference shows most. -->
            <!-- Rendered only when Stripe is actually behind the
                 subscription. A disabled button was the alternative and is
                 worse: it still says "Manage premium", so it reads as the
                 feature being broken rather than as billing living
                 somewhere else. -->
            <b-button
                v-if="hasStripeBilling"
                class="manage-premium"
                type="is-warning"
                :loading="openingBilling"
                @click="manageBilling">
                Manage premium
            </b-button>

            <!-- Billing is the one part of the site where a dead end costs
                 real money, and the portal is hosted by Stripe — so when it
                 does not show what someone expects, there is nothing on this
                 page for them to try next. A way to reach a human belongs
                 next to the button, not only in the footer. -->
            <p class="customizer-hint billing-help">
                Something look wrong?
                <a :href="supportServerUrl" target="_blank" rel="noopener noreferrer">
                    Ask us in the support server</a>.
            </p>
        </section>

        <!-- No Save button: a pick applies itself. The status is the
             receipt, and it's deliberately quiet — a snackbar on every
             swatch you tried would be worse than no feedback at all. -->
        <footer class="customizer-actions" aria-live="polite">
            <span v-if="saving" class="customizer-status">Saving…</span>
            <span v-else-if="savedRecently" class="customizer-status is-done">
                <b-icon icon="check" size="is-small" /> Saved
            </span>
        </footer>
</div>
</template>

<script>
import { themes } from '../../globals/profiles.js'
import { DISCORD_SUBSCRIPTION_HELP_URL, SUPPORT_SERVER_URL } from '../../globals/links.js'

/**
 * Why a route sent someone to this tab, in this app's words.
 *
 * A closed set, matched on the code alone. The text never comes from the
 * URL — see `billingNotice`.
 */
const BILLING_NOTICES = Object.freeze({
    'payment-problem': "Your last payment didn't go through, so we couldn't sell you another subscription. "
        + 'Update your card below and the existing one will pick up where it left off.',
})

/**
 * How long to wait after a pick before writing.
 *
 * Long enough to coalesce someone clicking along the swatches, short enough
 * that it still feels like the choice applied itself.
 */
const SAVE_DEBOUNCE_MS = 500

/** Matches DEFAULT_THEME_ID in config/profileThemes.js. */
const DEFAULT_THEME_ID = 'default'

/**
 * Title and background picker.
 *
 * Both lists come from the server, which decides what this user owns — the
 * component only renders them. Locked backgrounds are shown disabled rather
 * than hidden, so the shop has something to sell against, and the endpoint
 * re-checks ownership on save regardless of what gets clicked here.
 */
export default {
    name: 'ProfileCustomizer',
    props: {
        // The theme's card treatment, passed down rather than recomputed —
        // the parent owns which theme is applied. Matches ProfileStats.
        overlayStyles: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            selectedTitle: null,
            selectedTheme: 'default',
            selectedHidden: false,
            // Whether the last write landed, for the status line.
            savedRecently: false,
            // Set when a pick arrives mid-write; drives a follow-up save.
            resaveWhenFree: false,
            // Opening Stripe's portal is a round trip; the button says so.
            openingBilling: false,
        }
    },
    computed: {
        customization() { return this.$store.state.profile.customization },
        /** Whether there's a subscription to manage at all. */
        isPremium() { return this.$store.state.profile.premium === true },
        /** Whether there is billing to reach — see the section's own comment. */
        hasBilling() { return this.$store.state.profile.billing === true },
        /** Whether Stripe's portal is the right destination for this account. */
        hasStripeBilling() { return this.$store.state.profile.stripeBilling === true },
        /**
         * Copy for a `?billing=` code, or null.
         *
         * Keyed by code and never by prose from the URL — the same rule
         * PROFILE_ERRORS follows in the store. Rendering a query parameter
         * verbatim would let anyone put whatever sentence they liked on
         * someone else's billing page just by sending them a link.
         */
        billingNotice() {
            return BILLING_NOTICES[this.$route.query.billing] || null
        },
        paysViaDiscord() {
            const sources = this.$store.state.profile.activeSources
            return Array.isArray(sources) && sources.includes('discord')
        },
        supportServerUrl() { return SUPPORT_SERVER_URL },
        discordSubscriptionHelpUrl() { return DISCORD_SUBSCRIPTION_HELP_URL },
        saving() { return this.$store.state.profile.saving },
        titles() { return this.customization.titles },

        /** Server ownership joined with local presentation, keyed by id. */
        themeOptions() {
            return this.customization.themes.map(option => {
                const theme = themes[option.id]
                // The swatch paints the theme's own background, pattern
                // layer included, so the preview is the thing itself.
                const swatchStyle = { background: theme ? theme.colors.background : '#dbdbdb' }
                if (theme && theme.patternSpec) {
                    // Only a tiled pattern gets rescaled, and only a tiled
                    // pattern *can* be: at swatch size a full-size tile shows
                    // about one icon, which reads as a smudge. Keyed off the
                    // pattern rather than off `backgroundSize`, which since
                    // the scene tiers is no longer always a px tile — parsing
                    // `auto, cover` for a number yields `NaNpx`, and the three
                    // themes with a grid or stripe layer lost their swatch.
                    const tile = Math.round(parseInt(theme.backgroundSize, 10) * 0.45)
                    swatchStyle.backgroundSize = `${tile}px ${tile}px, cover`
                } else if (theme && theme.backgroundSize) {
                    swatchStyle.backgroundSize = theme.backgroundSize
                }

                return {
                    ...option,
                    rarity: theme ? theme.rarity : 'common',
                    swatchStyle,
                    overlay: theme ? theme.colors.overlay : 'rgba(255,255,255,0.7)',
                }
            })
                // What you own comes first. The list is long enough that the
                // handful you can actually apply were scattered through three
                // rows of locked ones, so choosing meant hunting. Sorting is
                // stable, so the tier order the server sends survives within
                // each group — owned still reads common → mythical, and so
                // does the locked half below it.
                .sort((a, b) => (b.owned ? 1 : 0) - (a.owned ? 1 : 0))
        },

        /**
         * The switch's v-model.
         *
         * A computed with a setter rather than binding `selectedHidden`
         * directly, so the write goes through the same queueSave() path as
         * the two pickers instead of needing a watcher of its own.
         */
        hideProfile: {
            get() { return this.selectedHidden },
            set(hidden) {
                this.selectedHidden = hidden
                this.queueSave()
            },
        },

        hasChanges() {
            const current = this.customization.current
            return this.selectedTitle !== current.title
                || this.selectedTheme !== current.theme
                || this.selectedHidden !== (current.hideProfile === true)
        },
    },
    watch: {
        // A pick is a save. Both go through queueSave rather than saving
        // directly so that clicking along a row of swatches sends one write
        // for where you landed, not one per swatch you passed through.
        selectedTitle() { this.queueSave() },
        selectedTheme(theme) {
            // Apply it to the page before the write goes out. The theme is
            // read from dbInfo.theme, so without this the panel, the page
            // frame and the card all sit unchanged until the round-trip
            // lands — the picker would look like the only thing that
            // noticed the click.
            this.$store.commit('setAppliedTheme', theme)
            this.queueSave()
        },
        // Follows the saved state. As a modal this only re-seeded on open;
        // a tab is mounted for as long as it's selected, so it tracks the
        // server instead — which also covers a save landing from elsewhere.
        'customization.current': {
            handler() {
                // A successful write refetches, which lands here. If the user
                // picked again while that write was in flight, resyncing would
                // throw their newer choice away and snap the page back to the
                // older one.
                //
                // `resaveWhenFree` is part of this and not an afterthought:
                // the refetch arrives in the gap *after* the write finishes
                // (`saving` already false, timer already cleared) but *before*
                // the deferred resave is queued. Guarding on the first two
                // alone leaves that gap open, and the newer pick is lost
                // exactly as if there were no guard at all.
                if (this.saveTimer || this.saving || this.resaveWhenFree) return
                this.syncFromSaved()
            },
            deep: true,
            immediate: true,
        },
    },
    created() {
        // Fetches what it renders, rather than relying on whoever mounts it
        // to have remembered. This used to hang off a `page` watcher on the
        // profile view; when the tabs became routes that watcher went away,
        // and the picker rendered its empty initial state — every background
        // missing, every title gone — which reads as a broken page rather
        // than as a missing fetch.
        this.$store.dispatch('profile/fetchCustomization')
    },
    beforeDestroy() {
        // Leaving mid-debounce must still write. See flush().
        if (!this.saveTimer) return
        clearTimeout(this.saveTimer)
        this.saveTimer = null
        this.flush()
    },
    methods: {
        /**
         * Cancelling, changing card, invoices — all of it lives in Stripe's
         * billing portal. Rebuilding any of it here would mean handling
         * payment details on our own origin, which we don't do.
         *
         * Moved from Premium.vue verbatim, guard included: without the
         * `finally` a failed round trip leaves the button spinning forever.
         */
        async manageBilling() {
            this.openingBilling = true
            try {
                await this.$store.dispatch('profile/manageBilling')
            } finally {
                this.openingBilling = false
            }
        },

        /**
         * Debounced, and only when something actually differs.
         *
         * The `hasChanges` guard is load-bearing beyond saving a request:
         * a successful write refetches, which re-runs syncFromSaved, which
         * reassigns the selections and re-fires these watchers. Without the
         * guard that's an endless save loop.
         */
        queueSave() {
            // Cleared first, unconditionally. Returning early on "nothing to
            // save" while a timer was still armed left it to fire against an
            // unchanged selection, which posts `{}` — and the endpoint answers
            // 400 "Nothing to change.", surfacing a red toast for a click the
            // user never made.
            clearTimeout(this.saveTimer)
            this.saveTimer = null
            if (!this.hasChanges) return

            this.savedRecently = false
            this.saveTimer = setTimeout(this.save, SAVE_DEBOUNCE_MS)
        },

        /** The diff to send, or null when the selection matches what's saved. */
        pendingChanges() {
            const current = this.customization.current
            const changes = {}
            if (this.selectedTitle !== current.title) changes.title = this.selectedTitle
            if (this.selectedTheme !== current.theme) changes.theme = this.selectedTheme
            if (this.selectedHidden !== (current.hideProfile === true)) {
                changes.hideProfile = this.selectedHidden
            }
            return Object.keys(changes).length ? changes : null
        },

        syncFromSaved() {
            const current = this.customization.current
            this.selectedTitle = current.title
            // A theme that has since been removed from the catalogue leaves
            // `current.theme` naming something with no swatch — the grid would
            // show nothing selected while the page renders the default. Show
            // what's actually applied instead.
            const known = this.customization.themes.some(t => t.id === current.theme)
            this.selectedTheme = known ? current.theme : DEFAULT_THEME_ID
            this.selectedHidden = current.hideProfile === true
        },
        async save() {
            clearTimeout(this.saveTimer)
            this.saveTimer = null

            // One write at a time. A pick made while a write is in flight has
            // to wait for it rather than race it — two overlapping saves land
            // in whatever order the network decides, and the loser silently
            // wins on the next refetch.
            if (this.saving) {
                this.resaveWhenFree = true
                return
            }

            // Re-checked here, not just in queueSave: the selection can be put
            // back between arming the timer and firing it.
            const changes = this.pendingChanges()
            if (!changes) return

            const saved = await this.$store.dispatch('profile/customize', changes)
            this.savedRecently = !!saved

            // The store already explains the failure (a 403 means the item
            // isn't actually owned). What it can't do is undo the optimistic
            // apply — leaving the picker, and the whole page, showing a theme
            // the server refused would be a lie about what's saved.
            if (!saved) {
                this.$store.commit('setAppliedTheme', this.customization.current.theme)
                this.syncFromSaved()
                this.resaveWhenFree = false
                return
            }

            // Something was picked while that write was in flight.
            if (this.resaveWhenFree) {
                this.resaveWhenFree = false
                this.queueSave()
            }
        },

        /**
         * Writes a queued edit without touching component state afterwards.
         *
         * Used on the way out: the panel is behind a `v-if`, so switching tabs
         * destroys it, and simply clearing the timer dropped the edit on the
         * floor — while `setAppliedTheme` had already made the page look like
         * it saved. Silent loss, visible only on the next reload.
         */
        flush() {
            const changes = this.pendingChanges()
            if (changes) this.$store.dispatch('profile/customize', changes)
        },
    },
}
</script>

<style scoped lang="scss">
.customizer {
    text-align: left;
    // `.dashboard-item` lays its children out as a centred, wrapping flex
    // row — right for the profile card's stat columns, wrong for a stack of
    // sections. As a modal this never applied; as a panel it does, and it
    // spread the heading, both sections and the footer across one row.
    display: block;
}

.customizer-title {
    font-size: 1.4rem;
    font-weight: bold;
    margin-bottom: 1.25rem;
}

.customizer-section {
    margin-bottom: 1.5rem;
}

.customizer-heading {
    font-size: 0.8rem;
    text-transform: uppercase;
    font-weight: bold;
    letter-spacing: 0.05em;
    margin-bottom: 0.15rem;
}

.customizer-hint,
.customizer-empty {
    font-size: 0.85rem;
    opacity: 0.7;
    margin-bottom: 0.75rem;
}

// Full opacity and a warning tint, unlike the hints around it — this is the
// answer to "why am I on this page", and it has to survive being landed on
// rather than read in sequence.
.billing-notice {
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
    padding: 0.6rem 0.75rem;
    border-left: 3px solid $warning;
    border-radius: 4px;
    background: rgba($warning, 0.12);
}

// Sits under the Manage button rather than above it, so the hint reads as a
// fallback for when the button did not do what was expected. Underlined
// because at 0.85rem and 70% opacity a colour change alone is not enough of
// a signal that it is a link.
.billing-help {
    margin-top: 0.75rem;
    margin-bottom: 0;

    a {
        text-decoration: underline;
    }
}

// Privacy

.privacy-toggle {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    cursor: pointer;
}

.privacy-toggle-text {
    display: block;

    // The hint is a block inside the label, so it stacks under the switch's
    // own text rather than running on from it.
    .customizer-hint {
        display: block;
        margin-bottom: 0;
        max-width: 34rem;
    }
}

.privacy-toggle-label {
    display: block;
    font-weight: bold;
    font-size: 0.95rem;
}

// Titles

.title-options {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

// Outlined on white until picked, filled once it is — the same hierarchy the
// profile header uses for the equipped title versus the rest. Filling every
// option meant a wall of saturated pills with nothing to say which was
// actually equipped; the 2px border alone wasn't carrying that.
.title-option {
    border: 2px solid currentColor;
    border-radius: 999px;
    padding: 0.3rem 0.85rem;
    font-size: 0.85rem;
    font-weight: bold;
    cursor: pointer;
    background-color: #fff;
    color: #363636;

    // Same tiers as the shop — see $rarity-* in variables.scss. Unselected
    // takes the tier as its outline and text, so the rarity still reads.
    &.is-common    { color: $rarity-common; }
    &.is-rare      { color: $rarity-rare; }
    &.is-epic      { color: $rarity-epic; }
    &.is-legendary { color: $rarity-legendary; }
    &.is-mythical  { color: $rarity-mythical; }

    // Selected drops `is-outlined`, so the global `.is-<tier>` rules in
    // variables.scss fill it with the tier gradient — the same fill the shop
    // and the equipped title on the profile use. Nothing per-tier is declared
    // here: those globals are gradients, and a `background-color` would sit
    // underneath one and never show.
    &.is-selected {
        color: #fff;
        // The fallback for "No title", which has no tier to fill with.
        background-color: #363636;
        border-color: transparent;
    }
}

// Backgrounds

.theme-options {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(7rem, 1fr));
    gap: 0.75rem;
}

.theme-option {
    border: 2px solid transparent;
    border-radius: 0.6rem;
    padding: 0.35rem;
    background: none;
    cursor: pointer;
    text-align: center;

    &.is-selected { border-color: $primary; }

    &.is-locked {
        cursor: not-allowed;
        opacity: 0.6;
    }
}

.theme-swatch {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 4rem;
    border-radius: 0.4rem;
    background-size: cover;
    overflow: hidden;
}

// A miniature of a profile card, so the swatch shows how readable the
// background actually is rather than just its colour.
.theme-swatch-card {
    width: 70%;
    height: 45%;
    border-radius: 0.25rem;
}

.theme-lock {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    color: #fff;
    text-shadow: 0 0 4px rgba(0, 0, 0, 0.8);
}

.theme-name {
    display: block;
    font-size: 0.75rem;
    font-weight: bold;
    margin-top: 0.35rem;
}

// The tier, carried on the name. Same $rarity-* scale the shop and the
// achievement pills use, so "legendary" means one thing across the product.
.theme-option {
    &.is-tier-common .theme-name    { color: $rarity-common; }
    &.is-tier-rare .theme-name      { color: $rarity-rare; }
    &.is-tier-epic .theme-name      { color: $rarity-epic; }
    &.is-tier-legendary .theme-name { color: $rarity-legendary; }
    &.is-tier-mythical .theme-name  { color: $rarity-mythical; }
}

.customizer-status {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.82rem;
    opacity: 0.65;

    &.is-done { opacity: 0.8; }
}

// Reserves the row even when there's nothing to say, so the panel doesn't
// jump by a line every time a save starts and finishes.
.customizer-actions {
    min-height: 1.6rem;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}
</style>
